// Shared rounding for commercial-ops CSV exports. Money is exported to the cent and rates (stored as
// fractions, so 0.0123 is 1.23%) to six decimal places, which keeps small conversion rates exact to
// 0.0001 percentage points without the long floating point tails BigQuery returns.
export const CSV_MONEY_DECIMALS = 2;
export const CSV_RATE_DECIMALS = 6;

function roundTo(value: unknown, decimals: number): number | null {
  if (value === null || value === undefined || value === '') return null;
  // BigQuery NUMERIC values can arrive wrapped as { value: '123.45' }.
  const raw = typeof value === 'object' && 'value' in value ? (value as { value: unknown }).value : value;
  const numeric = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(numeric)) return null;
  const factor = 10 ** decimals;
  // Math.round on the scaled value with an epsilon so 1.005 rounds to 1.01 rather than 1.00.
  const rounded = Math.round((numeric + Math.sign(numeric) * Number.EPSILON) * factor) / factor;
  return Object.is(rounded, -0) ? 0 : rounded;
}

export function csvMoney(value: unknown): number | null {
  return roundTo(value, CSV_MONEY_DECIMALS);
}

export function csvRate(value: unknown): number | null {
  return roundTo(value, CSV_RATE_DECIMALS);
}

/**
 * Rounds one measure by its kind. Money keeps at least cents and more when the page shows more (GM / Send
 * shows 3 places); percentages are fractions and keep six places; counts and other values pass through.
 */
export function csvMeasure(value: unknown, kind: string, pageDecimals = 0): unknown {
  if (kind === 'money') return roundTo(value, Math.max(CSV_MONEY_DECIMALS, pageDecimals));
  if (kind === 'percent' || kind === 'rate') return csvRate(value);
  return value;
}

/** File name with the reporting period, e.g. spp-conversion_2026-07-01_to_2026-10-21.csv. */
export function csvFileName(name: string, from?: string, to?: string): string {
  const period = from && to ? `_${from}_to_${to}` : from ? `_${from}` : '';
  return `${name}${period}.csv`;
}
